import { LineChart, type LineData, useColors, type XAxisPeriod } from 'ComponentLibrary'
import { isArray } from 'lodash'
import { IncomeChartBarLayer } from './IncomeChartBarLayer'
import { IncomeChartLegend } from './IncomeChartLegend'
import { IncomeChartTooltip } from './IncomeChartTooltip'
import { Headline } from 'ComponentLibrary/src/piazza/text/Headline/Headline'
import type { LineChartTooltipTranslations } from 'ComponentLibrary/src/molecules/Chart/LineChartTooltip'

export type IncomeChartProps = {
    series: LineData[]
    period: XAxisPeriod
    title: string
    currencySymbol: string
    showChange?: boolean
    yearOnly?: boolean
    yearFilterUnitLabel?: string
    trimLeadingZeroes?: boolean
    emptyStateTitle?: string
    tooltipTranslations?: LineChartTooltipTranslations
    locale?: string
}

export function IncomeChart({
    series,
    title,
    currencySymbol,
    period = 'year',
    showChange = false,
    yearOnly = false,
    yearFilterUnitLabel,
    trimLeadingZeroes = false,
    emptyStateTitle,
    tooltipTranslations,
    locale = 'de-DE',
}: IncomeChartProps) {
    const { getColor } = useColors()

    const mappedData = series.map((serie) => ({
        ...serie,
        data: serie.data.map(({ value, date, bars, ...rest }) => ({
            date,
            value: value !== undefined ? value / 100 : undefined, // cents to euros
            bars: isArray(bars)
                ? bars.map((bar, index) => ({
                      ...bar,
                      unit: currencySymbol,
                      color: bar.color || getColor(bars.length - index),
                      value: (bar.value as number) / 100, // cents to euros
                  }))
                : [],
            ...rest,
        })),
    }))

    const hasBars = mappedData.some((serie) => serie.data.some((point) => point.bars.length > 0))
    const hasMoreThanOneSerie = mappedData.length > 1

    return (
        <>
            <Headline variant="headline3" title={title} />
            <LineChart
                data={mappedData}
                yUnit={currencySymbol}
                period={period}
                showTrends={showChange}
                startAtZero={true}
                yearOnly={yearOnly}
                tooltip={(point) => <IncomeChartTooltip {...point} translations={tooltipTranslations} locale={locale} />}
                customLayer={hasBars ? IncomeChartBarLayer : undefined}
                customLegend={hasMoreThanOneSerie || hasBars ? IncomeChartLegend : undefined}
                withYearFilter
                yearFilterUnitLabel={yearFilterUnitLabel}
                trimLeadingZeroes={trimLeadingZeroes}
                emptyStateTitle={emptyStateTitle}
                tooltipTranslations={tooltipTranslations}
                locale={locale}
            />
        </>
    )
}
